import Image from 'next/image';
import {type FC, type PropsWithChildren, useMemo, useState} from 'react';
import type {SponsoredOption} from '.';
import FallbackThumbnail from '../../../../../../public/next-image/engineer/column_default.png';
import {cn} from '../../../../../lib/classname';
import {ClipButtonTypeD} from '../../../../../shared/components/ClipButton/TypeD';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {isNewPost} from '../../../usecases/isNewPost';
import {replaceEngineerCompanyName} from '../../../usecases/replaceEngineerCompanyName';
import {ColumnCardCompanyTag} from './CompanyTag';
import {ColumnCardTag} from './Tag';

type CardProps = {
  id: string;
  title: string;
  thumbnail?: string;
  clip: {
    handler: () => Promise<void>;
    isClipped: boolean;
    count: number;
  };
  componentId?: string;
  tagNames?: string[];
  className?: string;
  imageClassName?: string;
  imageSize?: {width: number; height: number};
  sponsoredOption: SponsoredOption;
  updatedAt?: string;
};

type TagSection = {
  displayNames: string[];
  totalCount: number;
};
export const CardPresentation: FC<CardProps> = ({
  id,
  title,
  thumbnail,
  clip,
  componentId,
  tagNames,
  className,
  imageClassName,
  imageSize,
  sponsoredOption,
  updatedAt,
}) => {
  const tagInfo = useMemo<TagSection>(() => {
    let names = tagNames ? [...tagNames] : [];
    // すでにタグにsponsorが含まれている場合は除外し、一番初めに追加する
    const hasSponsor = names.some((name) =>
      name.toLowerCase().includes('sponsor'),
    );
    names = names.filter((name) => !name.toLowerCase().includes('sponsor'));
    if (hasSponsor) names.unshift('sponsored記事');
    names = names.slice(0, 3);
    return {
      displayNames: names,
      totalCount: tagNames?.length ?? 0,
    };
  }, [tagNames]);

  const isNew = useMemo(
    () =>
      isNewPost({
        postDate: updatedAt ?? new Date('2000-01-01').toISOString(),
        currentDate: new Date().toISOString(),
      }),
    [updatedAt],
  );

  return (
    <div className={cn('flex flex-col justify-between', className)}>
      <CommonLink
        href={`/archives/${id}`}
        className="hover:opacity-60"
        data-gtm-click={componentId}
      >
        <CardImage
          thumbnail={thumbnail}
          title={title}
          className={imageClassName}
          size={imageSize}
          isNew={isNew}
        />
      </CommonLink>
      <div className="flex flex-col gap-8 py-12">
        <div className="flex justify-between items-center gap-x-4">
          <div className="overflow-x-hidden">
            {sponsoredOption.enabled ? (
              <ul className="flex gap-4">
                {sponsoredOption.companies.map((company) => (
                  <li key={company.id} className="inline-flex">
                    <ColumnCardCompanyTag
                      name={replaceEngineerCompanyName(company.name)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              tagInfo.displayNames.length > 0 && (
                <ul className="flex gap-4 line-clamp-1">
                  {tagInfo.displayNames.map((tagName) => (
                    <li key={tagName} className="inline-flex">
                      <ColumnCardTag name={tagName} />
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>
          <div className="ml-auto">
            <ClipButtonTypeD
              isClipped={clip.isClipped}
              handleClip={clip.handler}
              count={clip.count}
            />
          </div>
        </div>
        <CommonLink
          href={`/archives/${id}`}
          className="hover:opacity-60"
          data-gtm-click={componentId}
        >
          <h3
            className={cn(
              'text-base pc:text-md leading line-clamp-2',
              'h-[calc(2*1.5*14px)] pc:h-[calc(2*1.5*16px)]', // タイトルが短く1行分しかない場合でも高さを確保
            )}
          >
            {title}
          </h3>
        </CommonLink>
      </div>
    </div>
  );
};

type CardImageProps = {
  thumbnail?: string;
  title: string;
  className?: string;
  size?: {width: number; height: number};
  isNew: boolean;
};

const CardImage = ({
  thumbnail,
  title,
  className,
  size,
  isNew,
}: CardImageProps) => {
  const [error, setError] = useState<boolean>(false);

  if (error || !thumbnail) {
    return (
      <WithNew isNew={isNew} className="rounded-16">
        <Image
          className={cn('w-full object-cover', className)}
          src={FallbackThumbnail}
          alt={title}
          width={size?.width ?? 336}
          height={size?.height ?? 189}
        />
      </WithNew>
    );
  }

  return (
    <WithNew isNew={isNew} className="rounded-16">
      <Image
        className={cn('w-full object-cover', className)}
        src={thumbnail}
        alt={title}
        width={size?.width ?? 336}
        height={size?.height ?? 189}
        onError={() => {
          setError(true);
        }}
      />
    </WithNew>
  );
};

const WithNew: FC<PropsWithChildren<{isNew: boolean; className: string}>> = ({
  children,
  isNew,
  className,
}) => {
  return (
    <div className={cn('relative overflow-hidden', className)}>
      {children}
      {isNew && (
        <div
          className={cn(
            'absolute top-0 right-0',
            'w-[100px]',
            'rotate-45 translate-x-[25px] translate-y-[10px]',
            'bg-notification-1 text-white text-center',
          )}
        >
          <span className="font-bold">New</span>
        </div>
      )}
    </div>
  );
};
